'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { FileText, ArrowLeft } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

type Lang = 'en' | 'ar' | 'fr';

const translations = {
  en: {
    dir: 'ltr' as const,
    back: 'Back to Home',
    badge: 'Terms of Service',
    title: 'Terms of Service',
    updated: 'Last updated: August 2026',
    sections: [
      {
        heading: '1. Acceptance of Terms',
        body: 'By accessing or using WealthOS, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our service.',
      },
      {
        heading: '2. Description of Service',
        body: 'WealthOS is a personal wealth management platform that allows users to track assets, liabilities, financial goals, income, and generate financial reports. The service is provided "as is" and may be updated or modified at any time.',
      },
      {
        heading: '3. User Accounts',
        body: 'You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account.',
      },
      {
        heading: '4. Subscription Plans',
        body: 'WealthOS offers Free, Pro, and Pro Max subscription plans. Paid plans are billed in advance on a monthly or annual basis. You may cancel your subscription at any time; cancellation takes effect at the end of the current billing period.',
      },
      {
        heading: '5. Free Trial',
        body: 'New users may be eligible for a free trial period. At the end of the trial, you will be automatically enrolled in the selected paid plan unless you cancel before the trial ends.',
      },
      {
        heading: '6. Refund Policy',
        body: 'We offer refunds within 7 days of initial purchase if you are not satisfied with the service. Refund requests after this period are evaluated on a case-by-case basis. Contact support to request a refund.',
      },
      {
        heading: '7. Prohibited Uses',
        body: 'You may not use WealthOS for any unlawful purpose, to transmit harmful content, to attempt unauthorized access to our systems, or to violate any applicable laws or regulations.',
      },
      {
        heading: '8. Intellectual Property',
        body: 'All content, features, and functionality of WealthOS are owned by us and are protected by international copyright, trademark, and other intellectual property laws.',
      },
      {
        heading: '9. Disclaimer of Warranties',
        body: 'WealthOS is not a licensed financial advisor. The information provided is for informational purposes only and should not be considered financial advice. We make no warranties about the accuracy or completeness of the information provided.',
      },
      {
        heading: '10. Limitation of Liability',
        body: 'To the maximum extent permitted by law, WealthOS shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the service.',
      },
      {
        heading: '11. Changes to Terms',
        body: 'We reserve the right to modify these Terms of Service at any time. We will notify users of significant changes. Continued use of the service after changes constitutes acceptance of the new terms.',
      },
      {
        heading: '12. Contact',
        body: 'For questions about these Terms of Service, please contact us through the Support page.',
      },
    ],
  },
  ar: {
    dir: 'rtl' as const,
    back: 'العودة للرئيسية',
    badge: 'شروط الخدمة',
    title: 'شروط الخدمة',
    updated: 'آخر تحديث: أغسطس 2026',
    sections: [
      {
        heading: '١. قبول الشروط',
        body: 'بالوصول إلى WealthOS أو استخدامه، فإنك توافق على الالتزام بشروط الخدمة هذه وسياسة الخصوصية الخاصة بنا. إذا كنت لا توافق على هذه الشروط، يرجى عدم استخدام خدمتنا.',
      },
      {
        heading: '٢. وصف الخدمة',
        body: 'WealthOS هو منصة لإدارة الثروة الشخصية تتيح للمستخدمين تتبع الأصول والالتزامات والأهداف المالية والدخل وإنشاء التقارير المالية. تُقدَّم الخدمة "كما هي" وقد تُحدَّث أو تُعدَّل في أي وقت.',
      },
      {
        heading: '٣. حسابات المستخدمين',
        body: 'أنت مسؤول عن الحفاظ على سرية بيانات اعتماد حسابك وعن جميع الأنشطة التي تجري تحت حسابك. يجب إخطارنا فوراً بأي استخدام غير مصرح به لحسابك.',
      },
      {
        heading: '٤. خطط الاشتراك',
        body: 'يقدم WealthOS خطط اشتراك مجانية وPro وPro Max. تُفوتر الخطط المدفوعة مسبقاً على أساس شهري أو سنوي. يمكنك إلغاء اشتراكك في أي وقت؛ يسري الإلغاء في نهاية فترة الفوترة الحالية.',
      },
      {
        heading: '٥. الفترة التجريبية المجانية',
        body: 'قد يكون المستخدمون الجدد مؤهلين للحصول على فترة تجريبية مجانية. في نهاية الفترة التجريبية، سيتم تسجيلك تلقائياً في الخطة المدفوعة المختارة ما لم تلغِ قبل انتهاء الفترة التجريبية.',
      },
      {
        heading: '٦. سياسة الاسترداد',
        body: 'نقدم استردادات خلال 7 أيام من الشراء الأولي إذا لم تكن راضياً عن الخدمة. تُقيَّم طلبات الاسترداد بعد هذه الفترة على أساس كل حالة على حدة. تواصل مع الدعم لطلب الاسترداد.',
      },
      {
        heading: '٧. الاستخدامات المحظورة',
        body: 'لا يجوز لك استخدام WealthOS لأي غرض غير مشروع، أو نقل محتوى ضار، أو محاولة الوصول غير المصرح به إلى أنظمتنا، أو انتهاك أي قوانين أو لوائح معمول بها.',
      },
      {
        heading: '٨. الملكية الفكرية',
        body: 'جميع المحتوى والميزات ووظائف WealthOS مملوكة لنا ومحمية بموجب قوانين حقوق النشر والعلامات التجارية والملكية الفكرية الدولية.',
      },
      {
        heading: '٩. إخلاء مسؤولية الضمانات',
        body: 'WealthOS ليس مستشاراً مالياً مرخصاً. المعلومات المقدمة هي لأغراض إعلامية فقط ولا ينبغي اعتبارها نصيحة مالية. لا نقدم أي ضمانات حول دقة أو اكتمال المعلومات المقدمة.',
      },
      {
        heading: '١٠. تحديد المسؤولية',
        body: 'إلى أقصى حد يسمح به القانون، لن يكون WealthOS مسؤولاً عن أي أضرار غير مباشرة أو عرضية أو خاصة أو تبعية ناشئة عن استخدامك للخدمة.',
      },
      {
        heading: '١١. التغييرات على الشروط',
        body: 'نحتفظ بالحق في تعديل شروط الخدمة هذه في أي وقت. سنخطر المستخدمين بالتغييرات الجوهرية. يُعدّ الاستمرار في استخدام الخدمة بعد التغييرات قبولاً للشروط الجديدة.',
      },
      {
        heading: '١٢. التواصل',
        body: 'للاستفسار عن شروط الخدمة هذه، يرجى التواصل معنا عبر صفحة الدعم.',
      },
    ],
  },
  fr: {
    dir: 'ltr' as const,
    back: 'Retour à l\'accueil',
    badge: 'Conditions d\'utilisation',
    title: 'Conditions d\'utilisation',
    updated: 'Dernière mise à jour : Août 2026',
    sections: [
      {
        heading: '1. Acceptation des conditions',
        body: 'En accédant à WealthOS ou en l\'utilisant, vous acceptez d\'être lié par ces Conditions d\'utilisation et notre Politique de confidentialité. Si vous n\'acceptez pas ces conditions, veuillez ne pas utiliser notre service.',
      },
      {
        heading: '2. Description du service',
        body: 'WealthOS est une plateforme de gestion de patrimoine personnel qui permet aux utilisateurs de suivre les actifs, les passifs, les objectifs financiers, les revenus et de générer des rapports financiers. Le service est fourni "tel quel" et peut être mis à jour ou modifié à tout moment.',
      },
      {
        heading: '3. Comptes utilisateurs',
        body: 'Vous êtes responsable de la confidentialité de vos identifiants de compte et de toutes les activités qui se déroulent sous votre compte. Vous devez nous informer immédiatement de toute utilisation non autorisée de votre compte.',
      },
      {
        heading: '4. Plans d\'abonnement',
        body: 'WealthOS propose des plans d\'abonnement Gratuit, Pro et Pro Max. Les plans payants sont facturés à l\'avance sur une base mensuelle ou annuelle. Vous pouvez annuler votre abonnement à tout moment ; l\'annulation prend effet à la fin de la période de facturation en cours.',
      },
      {
        heading: '5. Essai gratuit',
        body: 'Les nouveaux utilisateurs peuvent bénéficier d\'une période d\'essai gratuite. À la fin de l\'essai, vous serez automatiquement inscrit au plan payant sélectionné, sauf si vous annulez avant la fin de l\'essai.',
      },
      {
        heading: '6. Politique de remboursement',
        body: 'Nous offrons des remboursements dans les 7 jours suivant l\'achat initial si vous n\'êtes pas satisfait du service. Les demandes de remboursement après cette période sont évaluées au cas par cas. Contactez le support pour demander un remboursement.',
      },
      {
        heading: '7. Utilisations interdites',
        body: 'Vous ne pouvez pas utiliser WealthOS à des fins illégales, pour transmettre du contenu nuisible, pour tenter un accès non autorisé à nos systèmes, ou pour violer toute loi ou réglementation applicable.',
      },
      {
        heading: '8. Propriété intellectuelle',
        body: 'Tout le contenu, les fonctionnalités et les fonctions de WealthOS nous appartiennent et sont protégés par les lois internationales sur le droit d\'auteur, les marques et autres droits de propriété intellectuelle.',
      },
      {
        heading: '9. Exclusion de garanties',
        body: 'WealthOS n\'est pas un conseiller financier agréé. Les informations fournies sont à titre informatif uniquement et ne doivent pas être considérées comme des conseils financiers. Nous ne faisons aucune garantie quant à l\'exactitude ou l\'exhaustivité des informations fournies.',
      },
      {
        heading: '10. Limitation de responsabilité',
        body: 'Dans la mesure maximale permise par la loi, WealthOS ne sera pas responsable des dommages indirects, accessoires, spéciaux ou consécutifs découlant de votre utilisation du service.',
      },
      {
        heading: '11. Modifications des conditions',
        body: 'Nous nous réservons le droit de modifier ces Conditions d\'utilisation à tout moment. Nous informerons les utilisateurs des changements importants. L\'utilisation continue du service après les modifications constitue une acceptation des nouvelles conditions.',
      },
      {
        heading: '12. Contact',
        body: 'Pour toute question sur ces Conditions d\'utilisation, veuillez nous contacter via la page Support.',
      },
    ],
  },
};

export default function TermsPage() {
  const [lang, setLang] = useState<Lang>('ar');
  const t = translations[lang];
  const isRtl = t.dir === 'rtl';

  return (
    <div className="min-h-screen bg-background" dir={t.dir}>
      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-border/50 bg-background/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href="/welcome" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} className={isRtl ? 'rotate-180' : ''} />
            {t.back}
          </Link>
          <div className="flex items-center gap-2">
            <AppLogo size={24} />
            <span className="text-sm font-semibold text-foreground">Wealth<span className="text-primary">OS</span></span>
          </div>
          <div className="flex items-center gap-1 bg-muted/50 rounded-xl p-1">
            {(['en', 'ar', 'fr'] as Lang[]).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                  lang === l ? 'bg-primary text-primary-foreground shadow' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-5">
            <FileText size={13} className="text-primary" />
            <span className="text-xs font-semibold text-primary">{t.badge}</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">{t.title}</h1>
          <p className="text-sm text-muted-foreground">{t.updated}</p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {t.sections.map((section) => (
            <div key={section.heading} className="glass-card rounded-2xl p-6 border border-border">
              <h2 className="text-base font-bold text-foreground mb-3">{section.heading}</h2>
              <p className="text-sm text-muted-foreground leading-relaxed">{section.body}</p>
            </div>
          ))}
        </div>

        {/* Footer links */}
        <div className="mt-12 pt-8 border-t border-border/50 flex flex-wrap items-center justify-center gap-6 text-xs text-muted-foreground">
          <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
          <Link href="/terms" className="text-primary font-semibold">Terms</Link>
          <Link href="/cookie-policy" className="hover:text-foreground transition-colors">Cookie Policy</Link>
          <Link href="/refund-policy" className="hover:text-foreground transition-colors">Refund Policy</Link>
          <Link href="/support" className="hover:text-foreground transition-colors">Support</Link>
        </div>
      </main>
    </div>
  );
}
